import { Injectable } from '@angular/core';
import {
  Event,
  EventMedia,
  Merchant,
  MerchantCategory,
  VisitorInfo,
  BlogCategory,
  Blog,
  SocialLink,
  HistoryCategory,
  History,
  ProductCategory,
  Product,
  SearchCategoryResults,
  MonthEvents,
  MerchantCategorySection,
  VisitorInfoCategorySection,
  HistoryCategorySection,
  VisitorInfoCategory, Page
} from './app.models';

import {
  BlogService,
  BlogList,
  Blog as CmsBlog,
  BlogCategory as CmsBlogCategory,
  BlogCategoryService,
  FlatPage as CmsPage,
  FlatPageService, FlatPageList,
  EventList, Event as CmsEvent,
  EventService
} from '../generated';

import { HttpClient } from '@angular/common/http';
import to from 'await-to-js';
import moment from 'moment';
import { environment } from '../environments/environment';

export const ASSET_BASE_URL = environment.config.ASSET_BASE_URL;

type ApiParams = {
  type?: string;
  url?: string;
  id?: number | string;
  path?: string;
  max?: number;
  fill?: boolean;
  categoryId?: string;
  categoryName?: string;
  subcategoryId?: string;
  subcategoryName?: string;
  featured?: boolean;
  random?: boolean;
  featuredOnHomePage?: boolean;
};

@Injectable()
export class AppDataService {
  API_BASE_URL = environment.config.API_BASE_URL;
  socialLinks: SocialLink[];
  blogData: Blog[];

  constructor(private httpClient: HttpClient,
              private cmsBlogService: BlogService,
              private cmsEventService: EventService,
              private cmsBlogCategoryService: BlogCategoryService,
              private flatPageService: FlatPageService) {
  }

  async get<T>(params: ApiParams) {
    const id = params.id;
    const path = params.path;
    const type = params.type;
    const url = params.url;
    const max = params.max;
    const fill = params.fill;
    const categoryId = params.categoryId;
    const subcategoryId = params.subcategoryId;
    const categoryName = params.categoryName;
    const subcategoryName = params.subcategoryName;
    const featured = params.featured;
    const random = params.random;
    const featuredOnHomePage = params.featuredOnHomePage;

    const p = [];
    if (!!id) {
      p.push(`id=${id}`);
    }
    if (!!path) {
      p.push(`path=${path}`);
    }
    if (!!type) {
      p.push(`type=${type}`);
    }
    if (!!url) {
      p.push(`url=${url}`);
    }
    if (max != null) {
      p.push(`max=${max}`);
    }
    if (!!fill) {
      p.push(`fill=${fill}`);
    }
    if (!!categoryId) {
      p.push(`categoryId=${categoryId}`);
    }
    if (!!subcategoryId) {
      p.push(`subcategoryId=${subcategoryId}`);
    }
    if (!!categoryName) {
      p.push(`categoryName=${categoryName}`);
    }
    if (!!subcategoryName) {
      p.push(`subcategoryName=${subcategoryName}`);
    }
    if (!!featured) {
      p.push(`featured=${featured}`);
    }
    if (!!random) {
      p.push(`random=${random}`);
    }
    if (!!featuredOnHomePage) {
      p.push(`featuredOnHomePage=${featuredOnHomePage}`);
    }

    const [err, response] = await to(this.httpClient.get<T>(`${this.API_BASE_URL}?${p.join('&')}`).toPromise());
    // console.log('get type', type, 'max', max, 'categoryId', categoryId, 'categoryName', categoryName, 'subcategoryId', subcategoryId,
    //   'subcategoryName', subcategoryName, 'featured', featured, 'featuredOnHomePage', featuredOnHomePage, response);

    if (!!err) {
      // console.log('error', err);
      return null;
    } else if (!!response['results']) {
      // console.log('got results', response['results']);
      return response['results'];
    } else {
      // console.log('returning entire response', response);
      return response;
    }
  }

  // EVENTS
  async getEvents(max?: number): Promise<Event[]> {
  //console.log('getEvents()');
    return await this.get<Event[]>({ type: 'events', max });
  }

  async getEvent(eventPath: string): Promise<Event> {
  //console.log('getEvent()', eventPath, eventPath);
    return await this.get<Event>({ type: 'events', path: eventPath });
  }

  async getEventPictures(max: number): Promise<EventMedia[]> {
    // console.log('getEventPictures()');
    return await this.get<EventMedia[]>({ type: 'eventPictures', max });
  }

  async getFeaturedEvents(max = 3): Promise<Event[]> {
    const events = await this.get<Event[]>({ type: 'events', featured: true, max });
  //console.log('getFeaturedEvents()', events);
    return events;
  }

  async getHomePageFeaturedEvents(max = 1): Promise<Event[]> {
    // console.log('getHomePageFeaturedEvent()');
    return await this.get<Event>({ type: 'events', featuredOnHomePage: true, max });
  }

  async getUpcomingEvents(max = 3): Promise<Event[]> {
    // console.log('getUpcomingEvents()');
    return await this.get<Event>({ type: 'upcomingEvents', max });
  }

  async getFeaturedEvent(): Promise<Event> {
    // console.log('getFeaturedEvent()');
    return (await this.get<Event>({ type: 'events', featured: true, max: 1 }))[0];
  }

  async getEventsByMonth(excludeFeatured = false): Promise<MonthEvents[]> {
    // console.log('getEventsByMonth()');
    return await this.get<MonthEvents[]>({ type: 'eventsByMonth', featured: excludeFeatured === false });
  }

  // MERCHANTS
  async getMerchantCategories(): Promise<MerchantCategory[]> {
  //console.log('getMerchantCategories()');
    return await this.get<MerchantCategory[]>({ type: 'merchantCategories' });
  }

  async getMerchants(categoryId?: string, subcategoryId?: string, max?: number): Promise<Merchant[]> {
    // console.log('getMerchants()');
    return await this.get<Merchant[]>({ type: 'merchants', categoryId, subcategoryId, max });
  }

  async getMerchant(merchantPath: string): Promise<Merchant> {
    // console.log('getMerchant()');
    const merchant = await this.get<Merchant>({ type: 'merchants', path: merchantPath });
  //console.log('getMerchant()', merchant, merchantPath);
    return merchant;
  }

  async getMerchantSectionsByCategory(categoryName?: string, subcategoryName?: string): Promise<MerchantCategorySection[]> {
    // console.log('getMerchantSectionsByCategory()');
  //console.log('merchant sections names', categoryName, subcategoryName);
    return await this.get<MerchantCategorySection[]>({ type: 'merchantSections', categoryName, subcategoryName });
  }

  async getFeaturedMerchants(max = 3): Promise<Merchant[]> {
    // console.log('getFeaturedMerchants()');
    return await this.get<Merchant[]>({ type: 'merchants', featured: true, max });
  }

  async getHomePageFeaturedMerchants(max = 5, fill = true): Promise<Merchant[]> {
    // console.log('getHomePageFeaturedMerchants()', max, fill);
    return await this.get<Merchant[]>({ type: 'merchants', featuredOnHomePage: true, max, fill });
  }

  async getFeaturedMerchant(): Promise<Merchant> {
    // console.log('getFeaturedMerchant()');
    return (await this.get<Merchant>({ type: 'merchants', featured: true, max: 1 }))[0];
  }

  // VISITOR INFO
  async getVisitorInfoCategories(): Promise<VisitorInfoCategory[]> {
    // console.log('getVisitorInfoCategories()');
    return await this.get<VisitorInfoCategory[]>({ type: 'visitorInfoCategories' });
  }

  async getVisitorInfoCategorySections(): Promise<VisitorInfoCategorySection[]> {
    // console.log('getVisitorInfoCategorySections()');
    return await this.get<VisitorInfoCategorySection[]>({ type: 'visitorInfoCategorySections' });
  }

  async getVisitorPageContent(visitorPath: string): Promise<VisitorInfo> {
    // console.log('getVisitorPageContent()');
    return await this.get<VisitorInfo>({ type: 'visitorInfo', path: visitorPath });
  }

  async getVisitorPageContents(max: number): Promise<VisitorInfo[]> {
    // console.log('getVisitorPageContent()');
    return await this.get<VisitorInfo>({ type: 'visitorInfo', max });
  }

  // BLOG
  async getBlogCategories(): Promise<BlogCategory[]> {
    const categories = await this.cmsBlogCategoryService.getOrgsOrgnameBlogcategory('global', true, false, false, null, null, 0, null, 0).toPromise();
    // console.log('getBlogCategories()');
    // return await this.get<BlogCategory[]>({ type: 'newsCategories' });
    return categories.map(category => {
      return {
        id: category.id,
        title: category.title,
        blogCategoryId: category.blogCategoryId
      };
    });
  }

  // FIXME - filter by category id
  async getBlogEntries(categoryId?: string, offset = 0, max = 3): Promise<Blog[]> {
    // if (!!this.blogData) {
    //   return this.blogData.slice(0, max);
    // }

    // console.log('getBlogEntries()');
    // return await this.get<Blog[]>({ type: 'news', categoryId, max });
    const blogs = await this.cmsBlogService.getOrgsOrgnameBlog('global', true, false, false, null,
      !!categoryId ? `blogCategory.id=${categoryId}` : null, offset, null, max, '-date').toPromise();
    return blogs.map(blog => this.convertBlog(blog));
    // return return this.blogData.slice(0, max);
  }

  async getBlogCount(categoryId?: string): Promise<number> {
    const res = await this.cmsBlogService.getOrgsOrgnameBlogCount('global', !!categoryId ? `blogCategory.id=${categoryId}` : null).toPromise();
    return res.count;
  }

  async getBlogEntry(path: string): Promise<Blog> {
    let blog: CmsBlog;

    blog = (await this.cmsBlogService.getOrgsOrgnameBlog('global', true, false, false, null, `path=${path}`, 0, null, 1).toPromise())[0];

    return this.convertBlog(blog);
    // console.log('getBlogEntry()');
    //     return await this.get<Blog>({ type: 'news', id });
  }

  convertBlog(blog: CmsBlog): Blog {
    return {
      id: blog.id,
      blogId: blog.blogId,
      blog_category: !!blog.blogCategory ? {
        id: blog.blogCategory.id,
        blogCategoryId: blog.blogCategory.blogCategoryId,
        title: blog.blogCategory.title
      } : null,
      pdf_link: blog.pdf,
      path: blog.path,
      title: blog.title,
      content: blog.content,
      description: blog.description,
      pdf_path: blog.pdf,
      youtube_embed_code: blog.youtubeEmbedCode,
      featured: blog.featured,
      publish_date: blog.date,
      sort_order: null,
      imageUrl: (blog.image as any).url,
      updated_date: blog.updatedAt
    } as Blog;
  }

  async getFeaturedBlogItems(max = 3): Promise<Blog[]> {
    const blogs = await this.cmsBlogService.getOrgsOrgnameBlog('global', true, false, false, null, `featured=true`, null, 1, max).toPromise();
    // console.log('getFeaturedBlogItems()');
    return blogs.map(blog => this.convertBlog(blog));
    // return await this.get<Blog[]>({ type: 'news', featured: true, max });
  }

  // PAGES
  async getPage(itemUrl: string): Promise<Page> {
    const pages = await this.flatPageService.getOrgsOrgnameFlatpage('global', true, false, null, null, `itemUrl=${itemUrl}`).toPromise();
    // console.log('getBlogEntry()');
    if (!pages || pages.length === 0) {
      return null;
    }
    return this.convertPage(pages[0]);
  }

  convertPage(page: CmsPage): Page {
    return {
      name: page.title,
      content: page.content,
      date_added: page.date,
      path: !!page.heroImage ? (page.heroImage as any).url : '',
      browser_title: page.browserTitle,
      meta_description: page.metaDescription,
      meta_keywords: page.metaKeywords,
      item_url: page.itemUrl,
      id: page.id
    } as Page;
  }

  // STORE
  async getProductCategories(): Promise<ProductCategory[]> {
    // console.log('getProductCategories()');
    return await this.get<ProductCategory[]>({ type: 'productCategories' });
  }

  async getProducts(categoryName?: string | string, max?: number): Promise<Product[]> {
  //console.log('getProducts()', categoryName);
    return await this.get<Product[]>({ type: 'products', max, categoryName });
  }

  async getProduct(productPath: string): Promise<Product> {
    // console.log('getProduct()');
    const product = await this.get<Product>({ type: 'products', path: productPath });
  //console.log('getProduct()', product, productPath);
    return product;
  }

  async getFeaturedProducts(max: number): Promise<Product[]> {
    // console.log('getFeaturedProducts()');
    return await this.get<Product[]>({ type: 'products', featured: true, max });
  }

  async getRandomProducts(max: number): Promise<Product[]> {
    // console.log('getFeaturedProducts()');
    return await this.get<Product[]>({ type: 'products', random: true, max });
  }

  // SOCIAL LINKS
  async getSocialLinks(): Promise<SocialLink[]> {
    // console.log('getSocialLinks()');

    if (!!this.socialLinks) {
      return this.socialLinks;
    }

    let socialLinks = await this.get<SocialLink[]>({ type: 'socialLinks' });

    // make sure it's not empty
    if (!socialLinks) {
      socialLinks = [];
    }

    // save for later
    this.socialLinks = socialLinks;

    return this.socialLinks;
  }

  async getSocialLink(name: string) {
    // console.log('getSocialLink()');
    return (await this.getSocialLinks()).find(l => l.title === name);
  }

  // HISTORY
  async getHistoryCategorySections(): Promise<HistoryCategorySection[]> {
    // console.log('getHistoryCategorySections()');
    return await this.get<HistoryCategorySection[]>({ type: 'historyCategorySections' });
  }

  async getHistoryCategories(): Promise<HistoryCategory[]> {
    // console.log('getHistoryCategories()');
    return await this.get<HistoryCategory[]>({ type: 'historyCategories' });
  }

  async getHistoryCategory(path: string): Promise<HistoryCategory> {
    // console.log('getHistoryCategory()');
    return await this.get<HistoryCategory>({ type: 'historyCategories', path });
  }

  async getHistory(categoryName?: string): Promise<History[]> {
    // console.log('getHistory()');
    return await this.get<History[]>({ type: 'historyCategories', categoryName });
  }

  async getCategoryHistory(): Promise<HistoryCategorySection[]> {
    // console.log('getCategoryHistory()');
    return await this.get<HistoryCategorySection[]>({ type: 'historyCategorySections' });
  }

  async search(searchString: string): Promise<SearchCategoryResults> {
    // console.log('search()', searchString);

    const results = await this.httpClient.get<any>(`${this.API_BASE_URL}?search=${searchString}`).toPromise();
    // console.log('search results', results);

    // events

    // merchants

    // market buzz

    // visitor info (attractions)

    return results;
  }
}


